import { Box, Grid, Typography } from '@mui/material'
import { CardBox } from '../../../theme/styleComponents'
import { CaseDetailRow } from '../../common/CaseDetailRow'
import { NOT_APPLICABLE } from '../../../constants'
import { ExternalUser } from '../../../../generated'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'
import { titleize } from '../../../utils'

interface ClientDetailCardProps {
  client?: ExternalUser | null
}

const ClientGeneralCard = ({ client }: ClientDetailCardProps) => {
  return (
    <Grid container spacing={5} my={1}>
      <Grid item md={7} lg={7} sm={12} xs={12} gap={3}>
        <DetailCard client={client} />
      </Grid>

      {client?.clientNumber && (
        <Grid item md={5} lg={5} sm={12} xs={12}>
          <FinancialCard client={client} />
        </Grid>
      )}

      {client?.addresses && client.addresses.length > 0 && (
        <Grid item md={client?.clientNumber ? 7 : 5} lg={client?.clientNumber ? 7 : 5} sm={12} xs={12} gap={3}>
          <AddressCard client={client} />
        </Grid>
      )}
    </Grid>
  )
}

const DetailCard = ({ client }: ClientDetailCardProps) => {
  const { type } = useParams()
  const {
    clientNumber,
    firstName,
    lastName,
    displayName,
    middleName,
    suffix,
    initials,
    dateOfBirth,
    personalPhone,
    phoneNumber,
    homePhone,
    email,
    isInternational,
    language,
    businessName,
    socialSecurityNumber,
    driverLicenseNumber
  } = client || {}
  return (
    <CardBox>
      <Box p={3} borderBottom="1px solid" borderColor="lightGrayThree">
        <Typography variant="h6" fontSize="18px">
          Client Details
        </Typography>
      </Box>
      {clientNumber ? (
        <CaseDetailRow title="Client#" content={clientNumber || NOT_APPLICABLE} />
      ) : (
        <CaseDetailRow title="Role" content={titleize(type || NOT_APPLICABLE)} />
      )}
      <CaseDetailRow
        title="Name"
        content={displayName || NOT_APPLICABLE}
      />
      <CaseDetailRow title="Initials" content={initials || NOT_APPLICABLE} />
      <CaseDetailRow title="International" content={isInternational ? 'Yes' : 'No'} />
      <CaseDetailRow title="Language" content={language ?? NOT_APPLICABLE} />
      <CaseDetailRow title="DOB" content={dateOfBirth ? dayjs(dateOfBirth).format('MM/DD/YYYY') : NOT_APPLICABLE} />
      <CaseDetailRow title="Work Phone" content={phoneNumber || NOT_APPLICABLE} />
      <CaseDetailRow title="Home Phone" content={homePhone || NOT_APPLICABLE} />
      <CaseDetailRow title="Personal Phone" content={personalPhone || NOT_APPLICABLE} />
      <CaseDetailRow title="Email" content={email || NOT_APPLICABLE} />
      {businessName && <CaseDetailRow title="Business Name" content={businessName} />}
      <CaseDetailRow title="SSN" content={socialSecurityNumber || NOT_APPLICABLE} />
      <CaseDetailRow title="Driver License #" content={driverLicenseNumber || NOT_APPLICABLE} />
    </CardBox>
  )
}

const FinancialCard = ({ client }: ClientDetailCardProps) => {
  const { lastPayDate, lastPayAmount, contractBalance, trustBalance, arrearsBalance, residualBalance } = client || {}
  return (
    <CardBox>
      <Box p={3} borderBottom="1px solid" borderColor="lightGrayThree">
        <Typography variant="h6" fontSize="18px">
          Financials
        </Typography>
      </Box>

      <CaseDetailRow title="Last Pay Date" content={lastPayDate || NOT_APPLICABLE} />
      <CaseDetailRow title="Contract Balance" content={contractBalance || NOT_APPLICABLE} />
      <CaseDetailRow title="Residual Balance" content={residualBalance || NOT_APPLICABLE} />
      <CaseDetailRow title="Trust Balance" content={trustBalance || NOT_APPLICABLE} />
      <CaseDetailRow title="Last Pay Amount" content={lastPayAmount || NOT_APPLICABLE} />
      <CaseDetailRow title="AR Balance" content={arrearsBalance || NOT_APPLICABLE} />
    </CardBox>
  )
}

const AddressCard = ({ client }: ClientDetailCardProps) => {
  const { addresses, isInternational } = client || {}
  return (
    <CardBox>
      <Box p={3} borderBottom="1px solid" borderColor="lightGrayThree">
        <Typography variant="h6" fontSize="18px">
          Address
        </Typography>
      </Box>
      {addresses?.map(address => {
        const { address1, address2, city, state, country, zipCode } = address || {}
        return (
          <>
            <CaseDetailRow title="Address Line 1" content={address1 || NOT_APPLICABLE} />
            <CaseDetailRow title="Address Line 2" content={address2 || NOT_APPLICABLE} />
            <CaseDetailRow title="City" content={city || NOT_APPLICABLE} />
            {!isInternational && <CaseDetailRow title="State" content={state || NOT_APPLICABLE} />}{' '}
            <CaseDetailRow title="Country" content={country || NOT_APPLICABLE} />
            <CaseDetailRow title="Zip Code" content={zipCode || NOT_APPLICABLE} />
          </>
        )
      })}
    </CardBox>
  )
}
export default ClientGeneralCard
